// Big SVG line chart for /trends. Renders an overall score time series with
// a sage "personal best" dot and amber "flagged" dots for sharp drops.
//
// Self-contained: takes pre-computed points and the visible y-range. The
// route is responsible for choosing the window and computing peaks/flags.

export type TrendPoint = {
  date: number; // unix seconds, increasing
  score: number;
  flag?: "peak" | "flag";
};

const W = 800;
const H = 240;
const PAD_L = 40;
const PAD_R = 40;
const PAD_T = 40;
const PAD_B = 30;
const INNER_W = W - PAD_L - PAD_R;
const INNER_H = H - PAD_T - PAD_B;

export type TrendChartProps = {
  points: TrendPoint[];
  /** y-axis range. Defaults to [45, 90]. */
  yMin?: number;
  yMax?: number;
};

export function TrendChart({ points, yMin = 45, yMax = 90 }: TrendChartProps) {
  if (points.length < 2) {
    return (
      <div className="kit-empty" style={{ minHeight: 240, display: "flex", alignItems: "center", justifyContent: "center" }}>
        Bring 2+ readings to see your trend line.
      </div>
    );
  }

  const xs = points.map((_p, i) => PAD_L + (i / (points.length - 1)) * INNER_W);
  const ys = points.map((p) => PAD_T + INNER_H - ((p.score - yMin) / (yMax - yMin)) * INNER_H);

  // Line + area paths.
  const linePath = points
    .map((_, i) => `${i === 0 ? "M" : "L"} ${xs[i]!.toFixed(1)} ${ys[i]!.toFixed(1)}`)
    .join(" ");
  const areaPath = `${linePath} L ${xs[xs.length - 1]!.toFixed(1)} ${(PAD_T + INNER_H).toFixed(1)} L ${xs[0]!.toFixed(1)} ${(PAD_T + INNER_H).toFixed(1)} Z`;

  // Y-axis ticks at quarter points.
  const ticks = [yMax, yMax - (yMax - yMin) / 3, yMax - (2 * (yMax - yMin)) / 3, yMin];

  // X-axis labels: first / mid / last.
  const xLabels = pickXLabels(points);

  return (
    <div className="chart-wrap">
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img" aria-label="Reading score over time">
        <g className="chart-grid">
          {ticks.map((t, i) => {
            const y = PAD_T + INNER_H - ((t - yMin) / (yMax - yMin)) * INNER_H;
            return <line key={i} x1={PAD_L} x2={W - PAD_R} y1={y} y2={y} />;
          })}
        </g>
        <g className="chart-axis">
          {ticks.map((t, i) => {
            const y = PAD_T + INNER_H - ((t - yMin) / (yMax - yMin)) * INNER_H;
            return (
              <text key={i} x={PAD_L - 6} y={y + 4} textAnchor="end">
                {Math.round(t)}
              </text>
            );
          })}
        </g>

        <path className="area-overall" d={areaPath} />
        <path className="line-overall" d={linePath} />

        {points.map((p, i) => {
          const cx = xs[i]!;
          const cy = ys[i]!;
          const cls = p.flag === "peak" ? "chart-dot peak" : p.flag === "flag" ? "chart-dot flag" : "chart-dot";
          const r = p.flag ? 4 : 3.2;
          return <circle key={i} className={cls} cx={cx} cy={cy} r={r} />;
        })}

        <g className="chart-axis">
          {xLabels.map((label, i) => (
            <text
              key={i}
              x={label.x}
              y={H - 8}
              textAnchor={label.anchor}
            >
              {label.text}
            </text>
          ))}
        </g>
      </svg>

      <div className="chart-legend">
        <span><i className="ink" />Overall score</span>
        <span><i className="peak" />Personal best</span>
        <span><i className="flag" />Flagged</span>
      </div>
    </div>
  );
}

function pickXLabels(points: TrendPoint[]): Array<{ x: number; text: string; anchor: "start" | "middle" | "end" }> {
  const fmt = (sec: number) =>
    new Date(sec * 1000).toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
  if (points.length === 0) return [];
  const first = points[0]!;
  const last = points[points.length - 1]!;
  const result: Array<{ x: number; text: string; anchor: "start" | "middle" | "end" }> = [
    { x: PAD_L, text: fmt(first.date), anchor: "start" },
  ];
  if (points.length >= 5) {
    const mid = points[Math.floor(points.length / 2)]!;
    result.push({
      x: PAD_L + INNER_W / 2,
      text: fmt(mid.date),
      anchor: "middle",
    });
  }
  result.push({ x: W - PAD_R, text: fmt(last.date), anchor: "end" });
  return result;
}
